import type { FC, KeyboardEvent } from "react";
import { useState } from "react";
import { parseError } from "../common/error";
import { Button } from "../components/Button";
import "./InboxReplyComposer.css";

interface InboxReplyComposerProps {
	content: string;
	setContent: (content: string) => void;
	onSend: (content: string) => Promise<void>;
	isSending: boolean;
	disabled?: boolean;
}

export const InboxReplyComposer: FC<InboxReplyComposerProps> = ({
	content,
	setContent,
	onSend,
	isSending,
	disabled,
}) => {
	const [error, setError] = useState<string | undefined>();

	const handleSend = async () => {
		if (!content.trim() || disabled || isSending) return;

		setError(undefined);
		try {
			await onSend(content.trim());
			setContent("");
		} catch (sendError) {
			setError(parseError(sendError) || "Failed to send reply.");
		}
	};

	const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
		if (e.key === "Enter" && !e.shiftKey) {
			e.preventDefault();
			handleSend();
		}
	};

	return (
		<div className="inbox-composer">
			{disabled ? (
				<div className="inbox-composer-disabled">
					This conversation is archived. Unarchive to reply.
				</div>
			) : (
				<>
					<div className="inbox-composer-row">
						<textarea
							className="inbox-composer-input"
							placeholder="Type your reply..."
							value={content}
							onChange={(e) => {
								setContent(e.target.value);
								if (error) setError(undefined);
							}}
							onKeyDown={handleKeyDown}
							rows={1}
							disabled={isSending}
						/>
						<Button
							onClick={handleSend}
							disabled={!content.trim() || isSending}
							loading={isSending}
							className="inbox-composer-send"
						>
							Send
						</Button>
					</div>
					{error && (
						<div className="inbox-composer-error" role="alert">
							{error}
						</div>
					)}
				</>
			)}
		</div>
	);
};
